import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import {
  MinistriesModel,
  MinistryModel,
  type UpdateMinistrySortOrderParamsSchema,
  UpsertMinistryRequestSchema,
} from '../models';
import { invalidateListMap, queryKeyMap } from '../utils';

export const useMinistries = <T = Array<z.infer<typeof MinistryModel>>>(
  select?: (data: z.infer<typeof MinistriesModel>) => T,
) => {
  return useQuery({
    queryKey: queryKeyMap.ministries(),
    queryFn: async () => {
      const data = await axiosGet('/api/ministries/');
      return MinistriesModel.parse(data);
    },
    select,
  });
};

export const useMinistryOptions = () => {
  return useQuery({
    queryKey: queryKeyMap.ministriesListAvailable(),
    queryFn: async () => {
      const data = await axiosGet('/api/ministries/list-available/');
      return MinistriesModel.parse(data);
    },
    select: (data) =>
      data.map((ministry) => ({
        label: ministry.name,
        value: ministry.id,
      })),
  });
};

export const useMinistry = (id: string) => {
  return useQuery({
    queryKey: queryKeyMap.ministry(id),
    queryFn: async () => {
      const data = await axiosGet(`/api/ministries/${id}/`);
      return MinistryModel.parse(data);
    },
    enabled: !!id,
  });
};

export const useCreateMinistry = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof UpsertMinistryRequestSchema>) => {
      const payload = UpsertMinistryRequestSchema.parse(values);
      return await axiosPost('/api/ministries/', payload);
    },
    onSuccess: async () => {
      await Promise.all(
        invalidateListMap.updateMinistries().map((key) => invalidate(key)),
      );
    },
  });
};

export const useUpdateMinistry = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof UpsertMinistryRequestSchema> & { id: string },
    ) => {
      const { id, ...payload } = UpsertMinistryRequestSchema.extend({
        id: z.string().uuid(),
      }).parse(values);
      return await axiosPatch(`/api/ministries/${id}/`, payload);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        ...invalidateListMap.updateMinistries().map((key) => invalidate(key)),
        invalidate(queryKeyMap.ministry(id)),
      ]);
    },
  });
};

export const useDeleteMinistry = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`/api/ministries/${id}/`);
    },
    onSuccess: async () => {
      await Promise.all(
        invalidateListMap.updateMinistries().map((key) => invalidate(key)),
      );
    },
  });
};

export const useChangeOrderOfMinistry = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      params: z.infer<typeof UpdateMinistrySortOrderParamsSchema>,
    ) => {
      return await axiosPost('/api/ministries/update-sort-order/', params);
    },
    onSuccess: async () => {
      await Promise.all(
        invalidateListMap.updateMinistries().map((key) => invalidate(key)),
      );
    },
  });
};
